import { Link } from 'react-router-dom';
import { useReadyAuth } from '../auth/use-auth';
import { SkelBar, SkelBadge, SkeletonPreviewLabel } from '../components/Skeleton';

/**
 * Home visual shell. Ports v0.7 HomePage structure (index.html line ~1880).
 *
 * 7.1d update: Drive for Five now renders v0.7-faithful move card
 * skeletons (checkbox circle + type-color left border + action title +
 * rationale + company/type label) instead of generic skeleton lines.
 */
export function Home() {
  const { profile } = useReadyAuth();
  const firstName = displayFirstName(profile.email, profile.display_name);
  const greeting = greetingForHour(new Date().getHours());
  const dateLine = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const quotes = [
    { text: 'The best salespeople listen more than they talk.', attribution: 'Anonymous' },
    {
      text: 'Opportunity is missed by most people because it is dressed in overalls and looks like work.',
      attribution: 'Thomas Edison',
    },
    {
      text: 'You miss 100 percent of the shots you do not take.',
      attribution: 'Wayne Gretzky',
    },
    { text: 'In the middle of difficulty lies opportunity.', attribution: 'Albert Einstein' },
  ];
  const day = new Date().getDate();
  const month = new Date().getMonth();
  const quote = quotes[(day + month * 31) % quotes.length];

  // v0.7 move types and their accent colors (index.html ~1885)
  const moveSkeletons = [
    { type: 'signal', borderColor: 'var(--bdse-green)', tagWidth: 88 },
    { type: 'pursuit', borderColor: 'var(--bdse-amber)', tagWidth: 72 },
    { type: 'contact_match', borderColor: 'var(--bdse-green)', tagWidth: 104 },
    { type: 'trend', borderColor: 'var(--bdse-indigo)', tagWidth: 96 },
  ];

  return (
    <div>
      <div className="bdse-hero">
        <div className="bdse-hero-row">
          <div>
            <h1 className="bdse-hero-title">
              {greeting}
              {firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="bdse-hero-subtitle">{dateLine}</p>
          </div>
        </div>

        <div className="bdse-hero-stats">
          <div className="bdse-home-stat">
            <div className="bdse-home-stat-value">0</div>
            <div className="bdse-home-stat-label">Active Signals</div>
          </div>
          <div className="bdse-home-stat">
            <div className="bdse-home-stat-value">0</div>
            <div className="bdse-home-stat-label">Act Now</div>
          </div>
          <div className="bdse-home-stat">
            <div className="bdse-home-stat-value">0</div>
            <div className="bdse-home-stat-label">Your Watchlist</div>
          </div>
        </div>
      </div>

      <div className="bdse-drive-lead">
        Your Drive for Five represents the <strong>10% of effort</strong> that
        drives <strong>90% of results</strong>.
      </div>

      <section className="bdse-drive-five">
        <header className="bdse-drive-five-header">
          <div>
            <h2 className="bdse-drive-five-title">Drive for Five</h2>
            <p className="bdse-drive-five-sub">
              Daily prioritization: five concrete next moves
            </p>
          </div>
          <button
            type="button"
            disabled
            title="Coming in Stream 3D"
            className="bdse-btn-secondary-small"
          >
            Generate moves
          </button>
        </header>

        <SkeletonPreviewLabel text="Sample preview - daily moves populate from Stream 3D" />

        <div className="bdse-drive-move-list">
          {moveSkeletons.map((m, i) => (
            <article
              key={i}
              className="bdse-drive-move-card bdse-skeleton-card"
              style={{ borderLeft: `3px solid ${m.borderColor}` }}
            >
              <div className="bdse-drive-move-check" aria-hidden="true">
                <div className="bdse-drive-move-check-box" />
              </div>

              <div className="bdse-drive-move-body">
                <div className="bdse-drive-move-action">
                  <SkelBar w={i % 2 ? '72%' : '64%'} h={12} />
                </div>
                <div className="bdse-drive-move-rationale">
                  <SkelBar w="90%" h={8} style={{ marginTop: 6 }} />
                  <SkelBar w="56%" h={8} style={{ marginTop: 4 }} />
                </div>
                <div className="bdse-drive-move-meta">
                  <SkelBadge w={m.tagWidth} h={14} />
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="bdse-drive-five-foot">
          Stream 3D populates daily moves from your active signals, pursuits,
          contacts, and deadlines.
        </p>
      </section>

      <section className="bdse-quote-glass">
        <p className="bdse-quote-text">&ldquo;{quote?.text ?? 'Business development is the discipline of turning weak signals into timely action.'}&rdquo;</p>
        <p className="bdse-quote-attr">- {quote?.attribution ?? 'BD Signal Engine'}</p>
      </section>

      <section className="bdse-home-tiles">
        <Link to="/signals" className="bdse-home-tile">
          <div className="bdse-home-tile-eyebrow">Signal review</div>
          <div className="bdse-home-tile-title">Market Signals</div>
          <p className="bdse-home-tile-body">
            AI-scored opportunities from eight data sources. Tiered into Act
            Now, Act This Week, and Monitor.
          </p>
        </Link>
        <Link to="/pursuits" className="bdse-home-tile">
          <div className="bdse-home-tile-eyebrow">Pursuit Tracker</div>
          <div className="bdse-home-tile-title">Active pursuits</div>
          <p className="bdse-home-tile-body">
            Claimed signals with status, notes, and pipeline view.
          </p>
        </Link>
        <Link to="/contacts" className="bdse-home-tile">
          <div className="bdse-home-tile-eyebrow">Contact CRM</div>
          <div className="bdse-home-tile-title">Your contacts</div>
          <p className="bdse-home-tile-body">
            Decision-makers and warm leads with last-contact and temperature.
          </p>
        </Link>
        <Link to="/intel" className="bdse-home-tile">
          <div className="bdse-home-tile-eyebrow">Market intel</div>
          <div className="bdse-home-tile-title">Intel to Influence</div>
          <p className="bdse-home-tile-body">
            Trends, articles, hot topics, and thought-piece building.
          </p>
        </Link>
      </section>
    </div>
  );
}

function displayFirstName(email: string, displayName: string | null): string {
  if (displayName && displayName.trim()) return displayName.trim().split(' ')[0] ?? 'there';
  const local = email.split('@')[0] ?? 'there';
  const first = local.split('.')[0] ?? 'there';
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
